import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Section, Breadcrumb } from "../components/PageShell";
import { BUSINESS_AREAS } from "../data/site";
import { ArrowRight, Check } from "lucide-react";

const BusinessAreaDetail = () => {
    const { slug } = useParams();
    const area = BUSINESS_AREAS.find((b) => b.slug === slug);
    if (!area) return <Navigate to="/business-areas" replace />;

    return (
        <>
            {/* Hero image */}
            <section className="relative">
                <div className="aspect-[21/9] lg:aspect-[21/7] w-full overflow-hidden bg-[var(--ns-paper)]">
                    <img
                        src={area.image}
                        alt={area.title}
                        className="w-full h-full object-cover grayscale"
                    />
                </div>
            </section>

            <Section>
                <Breadcrumb
                    items={[
                        { label: "Business Areas", to: "#/business-areas" },
                        { label: area.title },
                    ]}
                />

                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-7">
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-[64px] font-bold leading-[1.05] tracking-tight"
                            style={{ color: "var(--ns-ink)" }}
                            data-testid="area-title"
                        >
                            {area.title}
                        </h1>
                        <p
                            className="font-body mt-8 text-lg leading-relaxed"
                            style={{ color: "var(--ns-body)" }}
                            data-testid="area-intro"
                        >
                            {area.intro}
                        </p>

                        {area.callout && (
                            <div className="mt-10 border border-[var(--ns-border)] bg-[#fafaf8] p-7">
                                <div
                                    className="font-body text-[11px] uppercase tracking-[0.22em] mb-3"
                                    style={{ color: "var(--ns-muted)" }}
                                >
                                    {area.callout.label}
                                </div>
                                <p
                                    className="font-body text-base leading-relaxed mb-4"
                                    style={{ color: "var(--ns-body)" }}
                                >
                                    {area.callout.text}
                                </p>
                                <a
                                    href={area.callout.ctaHref}
                                    className="font-body text-sm font-medium inline-flex items-center gap-2 hover:underline"
                                    style={{ color: "var(--ns-ink)" }}
                                >
                                    {area.callout.ctaLabel} <ArrowRight size={14} />
                                </a>
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-5">
                        <div
                            className="font-body text-[11px] uppercase tracking-[0.22em] mb-5"
                            style={{ color: "var(--ns-muted)" }}
                        >
                            Scope of Activities
                        </div>
                        <ul className="space-y-4">
                            {area.scope.map((s, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 pb-4 border-b border-[var(--ns-border)] last:border-0"
                                    data-testid={`area-scope-${i}`}
                                >
                                    <Check
                                        size={16}
                                        strokeWidth={2.2}
                                        className="mt-1 shrink-0"
                                        style={{ color: "var(--ns-ink)" }}
                                    />
                                    <span
                                        className="font-body text-[15px]"
                                        style={{ color: "var(--ns-body)" }}
                                    >
                                        {s}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            {/* Focus */}
            <section className="border-t border-[var(--ns-border)] bg-[#fafaf8]">
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <div
                                className="font-body text-[11px] uppercase tracking-[0.22em]"
                                style={{ color: "var(--ns-muted)" }}
                            >
                                Our Focus
                            </div>
                        </div>
                        <div className="lg:col-span-8">
                            <p
                                className="font-display text-2xl lg:text-3xl leading-snug tracking-tight"
                                style={{ color: "var(--ns-ink)" }}
                            >
                                {area.focus}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Enquiries strip */}
            <section className="border-t border-[var(--ns-border)] bg-[var(--ns-ink)]">
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                        <span
                            className="font-body text-[11px] uppercase tracking-[0.22em]"
                            style={{ color: "rgba(255,255,255,0.6)" }}
                        >
                            Business Enquiries
                        </span>
                        <h3
                            className="font-display mt-3 text-2xl lg:text-3xl font-bold tracking-tight"
                            style={{ color: "#fff" }}
                        >
                            Discuss your {area.title.toLowerCase()} requirements.
                        </h3>
                    </div>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-3 bg-white text-[var(--ns-ink)] px-7 py-4 font-body text-sm font-medium hover:bg-[#f0f0ec] transition-colors"
                    >
                        Contact <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </>
    );
};

export default BusinessAreaDetail;
